import { useEffect, useState } from 'react'
import { Box, Button, Card, Stack, Typography } from '@mui/material'
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded'
import { useAppData } from '../contexts/AppDataContext'
import { pushService } from '../services/push'

/**
 * Lets an attendee opt in to push notifications for the event. Hidden entirely
 * when push isn't configured or the browser doesn't support it.
 */
export default function NotifyOptIn() {
  const { event } = useAppData()
  const [available, setAvailable] = useState(false)
  const [subscribed, setSubscribed] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let active = true
    ;(async () => {
      const cfg = await pushService.getConfig()
      const supported =
        'serviceWorker' in navigator && 'PushManager' in window && cfg.enabled
      if (!active) return
      setAvailable(supported)
      if (supported) setSubscribed(await pushService.isSubscribed())
    })()
    return () => {
      active = false
    }
  }, [])

  if (!available || !event) return null

  const enable = async () => {
    setBusy(true)
    try {
      const ok = await pushService.subscribe(event.id)
      setSubscribed(ok)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card variant="outlined" sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            bgcolor: 'rgba(0,117,188,0.1)',
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <NotificationsActiveRoundedIcon />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Event notifications
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {subscribed
              ? "You'll get alerts for room changes and updates."
              : 'Get alerts for room changes and important updates.'}
          </Typography>
        </Box>
        <Button
          variant={subscribed ? 'outlined' : 'contained'}
          size="small"
          disabled={busy || subscribed}
          onClick={enable}
        >
          {subscribed ? 'On' : busy ? 'Enabling…' : 'Enable'}
        </Button>
      </Stack>
    </Card>
  )
}
